import React, { useEffect, useMemo, useState } from 'react';
import type { GeographyType, TransportationPeerMetric } from '../../services/transportationPeerComparisonService';
import { getTransportationPeerComparison } from '../../services/transportationPeerComparisonService';

function formatPct(v: number | null) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(0)}%`;
}

function formatGap(v: number | null, evLabelMode = false) {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  const label = evLabelMode ? 'pp' : 'pp';
  return `${sign}${v.toFixed(0)} ${label}`;
}

const TransportationPeerComparisonCard: React.FC<{
  locationName: string;
  geofips: string | number | null;
  geographyType: GeographyType;
  year?: number;
}> = ({ locationName, geofips, geographyType, year = 2023 }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof getTransportationPeerComparison>>>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getTransportationPeerComparison({
          locationName,
          geographyType,
          geofips,
          year
        });
        if (!cancelled) setResult(res);
      } catch (e: any) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load comparison');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [locationName, geographyType, geofips, year]);

  const driversText = useMemo(() => {
    if (!result) return null;
    if (!result.topDrivers.length) return 'No strong peer drivers found for this location.';
    return result.topDrivers
      .map((d) => d.label)
      .slice(0, 2)
      .join(' and ');
  }, [result]);

  if (loading) {
    return (
      <div className="overview-text-secondary" style={{ padding: '20px', textAlign: 'center' }}>
        Loading transportation peer comparison...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: '#b91c1c', padding: '20px' }}>
        {error}
      </div>
    );
  }

  if (!result) {
    return (
      <div className="overview-text-secondary" style={{ padding: '20px' }}>
        No peer comparison available for {locationName}.
      </div>
    );
  }

  const evMetricKey: TransportationPeerMetric['key'] = 'ev_charging_percentile';

  return (
    <div>
      <div style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Compared within {result.densityPeerGroupLabel} (N={result.peerGroupN}). Statewide percentiles use the same underlying livability percentiles; peer percentiles are ranked within this density group.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
        {result.metrics.map((m) => {
          const isEV = m.key === evMetricKey;
          return (
            <div
              key={m.key}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.45)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{m.label}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Gap vs peer median: {formatGap(m.gapFromPeerMedian, isEV)}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 4 }}>Statewide</div>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '1.05rem' }}>
                    {formatPct(m.statewidePercentile)}
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 4 }}>Peers</div>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '1.05rem' }}>
                    {formatPct(m.peerPercentile)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Top peer drivers</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          {driversText ? driversText + '.' : '—'}
        </div>
      </div>
    </div>
  );
};

export default TransportationPeerComparisonCard;

